import React, { createContext, useContext, useMemo, useReducer } from 'react'
import { useChannels, useUserRecordings } from '../hooks'
import { Channel, Recording } from '../types'
import { timelineReducer, TimelineState } from '../reducers/view-controls'

interface AppContextType {
  timeline: TimelineState
  initTimeline: (offsetX: number, date: Date) => void
  setOffsetFromUser: (x: number) => void
  setOffsetProgrammatic: (x: number) => void
  setOffsetProgrammaticChannel: (x: number) => void
  setTouched: (t: boolean) => void
  setDateFromHeader: (date: Date) => void
  setOriginalOffset: (x: number) => void
  setSource: (s: 'user-scroll' | 'programmatic' | 'header' | null) => void

  offsetX: number
  originalOffset: number

  userRecordings: Recording[] | null
  setUserRecordings: React.Dispatch<React.SetStateAction<Recording[] | null>>
  isUserRecordingsLoading: boolean
  isUserRecordingsError: Error | null
  isProgramRecording: (programId: number) => 'once' | 'everyday' | 'none'
  refetchRecordings: () => void

  channelData: Channel[] | undefined
  channelIsLoading: boolean
  channelError: Error | null

  days: string[]
}

const AppContext = createContext<AppContextType | undefined>(undefined)

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // recordings
  const {
    data: userRecordings,
    setData: setUserRecordings,
    isLoading: isUserRecordingsLoading,
    refetch: refetchRecordings,
    error: isUserRecordingsError,
  } = useUserRecordings()

  // channels
  const {
    data: channelData,
    isLoading: channelIsLoading,
    error: channelError,
  } = useChannels()

  const daysInterval = Number(import.meta.env.VITE_DAYS_INTERVAL) || 1
  const offset = Math.floor(daysInterval / 2)
  const days = useMemo(
    () =>
      Array.from({ length: daysInterval }, (_, i) => {
        const date = new Date()
        date.setDate(date.getDate() + (i - offset))
        return date.toISOString().slice(0, 10)
      }),
    [daysInterval, offset]
  )
  const initialTimeline: TimelineState = {
    offsetX: 0,
    originalOffset: 0,
    touched: false,
    dateDisplayed: new Date(),
    source: null,
    days: Array.from({ length: daysInterval }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() + (i - offset))
      return date.toISOString().slice(0, 10)
    }),
  }
  const [timeline, dispatchTimeline] = useReducer(
    timelineReducer,
    initialTimeline
  )

  const initTimeline = (offsetX: number, date: Date) =>
    dispatchTimeline({
      type: 'INIT',
      payload: { offsetX, dateDisplayed: date },
    })
  const setOffsetFromUser = (x: number) =>
    dispatchTimeline({ type: 'SCROLL_USER', payload: { offsetX: x } })
  const setOffsetProgrammatic = (x: number) =>
    dispatchTimeline({ type: 'SCROLL_PROGRAMMATIC', payload: { offsetX: x } })
  const setTouched = (t: boolean) =>
    dispatchTimeline({ type: 'SET_TOUCHED', payload: t })
  const setDateFromHeader = (date: Date) =>
    dispatchTimeline({ type: 'SET_DATE_FROM_HEADER', payload: { date } })
  const setOriginalOffset = (x: number) =>
    dispatchTimeline({ type: 'SET_ORIGINAL_OFFSET', payload: x })
  const setOffsetProgrammaticChannel = (x: number) =>
    dispatchTimeline({ type: 'SET_OFFSET_PROGRAMMATIC_CHANNEL', payload: x })
  const setSource = (s: 'user-scroll' | 'programmatic' | 'header' | null) =>
    dispatchTimeline({ type: 'SET_SOURCE', payload: s })

  const isProgramRecording = (programId: number) => {
    const recording = userRecordings?.find(
      (rec) =>
        rec.programId === programId ||
        rec.scheduledPrograms?.some((sp) => sp.programId === programId)
    )
    return recording?.frequency || 'none'
  }

  return (
    <AppContext.Provider
      value={{
        // timeline
        timeline,
        initTimeline,
        setOffsetFromUser,
        setOffsetProgrammatic,
        setTouched,
        setDateFromHeader,
        setOriginalOffset,
        setOffsetProgrammaticChannel,
        setSource,
        offsetX: timeline.offsetX,
        originalOffset: timeline.originalOffset,

        // recordings/channels
        userRecordings,
        setUserRecordings,
        isUserRecordingsLoading,
        isUserRecordingsError,
        isProgramRecording,
        refetchRecordings,
        channelData,
        channelIsLoading,
        channelError,

        days,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export const useApp = () => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within a AppProvider')
  }
  return context
}
